import * as Sentry from "@sentry/react";
import cuid from "cuid";

import { createLogger } from "@/logger";

const log = createLogger("reset");

import { DateTime, Duration } from "luxon";

import { ampli, flags } from "@/config";
import { applyContentRetention, getRetentionConfig } from "@/services/ContentRetention";
import { applyFeedEviction } from "@/services/FeedEviction";
import { type IdleTransition, InactivityMonitorService } from "@/services/InactivityMonitorService.ts";
import { recordEventAndFlush } from "@/services/telemetry";
import { store } from "@/store/store";
import { safeSetItem } from "@/utils/localStorage";

const EnableEvictionFlag = "ResetCoordinator.EnableEviction";
const EnableContentRetentionFlag = "webWSConfigRetention";

export type ResetCoordinatorConfig = {
  intervalMs: number;
};

const defaultConfig: ResetCoordinatorConfig = {
  intervalMs: Duration.fromObject({ minutes: 1 }).as("milliseconds"),
};
type UserMetadata = {
  accountId?: string;
  workspaceId?: string;
  workspaceMembershipId?: string;
};

export class ResetCoordinator {
  private static instance: ResetCoordinator | null = null;
  private static readonly EVICTION_COOLDOWN_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");
  private static readonly EVICTION_IDLE_THRESHOLD_MS = Duration.fromObject({ seconds: 30 }).as("milliseconds");
  private static readonly CONTENT_RETENTION_COOLDOWN_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");
  private static readonly CONTENT_RETENTION_STORAGE_KEY = "lastContentRetentionRun";

  private config: ResetCoordinatorConfig = defaultConfig;
  private contentRetentionRunning = false;
  private evictionRunning = false;
  private inactivityMonitorService: InactivityMonitorService;
  private isStarted: boolean = false;
  private lastEvictionRun = DateTime.fromMillis(0);
  private sessionId: string;
  private sessionIndex: number;
  private timer: number | null = null;
  private userMetadata: UserMetadata = {};

  public static getInstance(): ResetCoordinator {
    if (!ResetCoordinator.instance) {
      ResetCoordinator.instance = new ResetCoordinator();
      ResetCoordinator.instance.setInactivityMonitorService(new InactivityMonitorService());
    }
    return ResetCoordinator.instance;
  }

  public setConfig(config: Partial<ResetCoordinatorConfig> = {}): void {
    this.config = { ...defaultConfig, ...config };
  }

  public setInactivityMonitorService(inactivityMonitorService: InactivityMonitorService): void {
    this.inactivityMonitorService = inactivityMonitorService;
  }

  public updateUserMetadata(partial: UserMetadata): void {
    log.info(`[${this.constructor.name}] Updating user metadata`, { partial });
    this.userMetadata = { ...this.userMetadata, ...partial };
  }

  public async start(): Promise<void> {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    this.sessionId = cuid();
    this.sessionIndex = 0;
    if (!this.inactivityMonitorService) {
      throw new Error("InactivityMonitorService not set");
    }

    this.inactivityMonitorService.start();
    log.info(`[${this.constructor.name}] ResetCoordinator started`, { ...this.config });

    await this.tick();
  }

  private scheduleNext(): void {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(
      () =>
        this.tick().catch((error) => {
          log.error(`[${this.constructor.name}] Error collecting metrics`, { error });
        }),
      this.config.intervalMs,
    );
  }

  private async tick(): Promise<void> {
    const idleTransition: IdleTransition | null = this.inactivityMonitorService.consumeIdleTransition();

    await this.maybeRunEviction();
    if (idleTransition) {
      await this.maybeRunContentRetention(idleTransition);
    }
    this.updateStoreSentryContext();

    this.sessionIndex++;
    this.scheduleNext();
  }

  /** Row counts of the largest collections, attached as Sentry context for later captures. */
  private updateStoreSentryContext(): void {
    try {
      const stats = store.stats();
      const topCollections = Object.entries(stats)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .reduce(
          (acc, [name, rows]) => {
            acc[`${name}_rows`] = rows;
            return acc;
          },
          {} as Record<string, number>,
        );
      Sentry.setContext("store_collections", topCollections);
    } catch (error) {
      log.warn(`[${this.constructor.name}] Failed to update store Sentry context`, { error });
    }
  }

  private async maybeRunEviction(): Promise<void> {
    if (this.evictionRunning) return;

    const evictionEnabled = flags[EnableEvictionFlag];
    if (!evictionEnabled) return;

    const inactivityMs = this.inactivityMonitorService.inactivityDurationMs;
    if (inactivityMs < ResetCoordinator.EVICTION_IDLE_THRESHOLD_MS) return;

    if (DateTime.now().diff(this.lastEvictionRun).toMillis() < ResetCoordinator.EVICTION_COOLDOWN_MS) return;

    this.evictionRunning = true;
    try {
      const result = await applyFeedEviction();
      this.lastEvictionRun = DateTime.now();

      if (result.deletedItems > 0) {
        log.debug(`[${this.constructor.name}] Feed eviction complete`, {
          deletedItems: result.deletedItems,
          deletedContentIds: result.deletedContentIds,
          affectedFeeds: result.affectedFeeds,
        });
        await recordEventAndFlush("db.eviction.completed", {
          db: {
            eviction: {
              deletedItems: result.deletedItems,
              deletedContentIds: result.deletedContentIds,
              affectedFeeds: result.affectedFeeds,
              user: this.userMetadata,
            },
          },
        });
      }
    } catch (error) {
      log.error(`[${this.constructor.name}] Feed eviction failed`, { error });
    } finally {
      this.evictionRunning = false;
    }
  }

  private async maybeRunContentRetention(trigger: "idle-enter" | "idle-exit"): Promise<void> {
    if (this.contentRetentionRunning) return;

    const retentionEnabled = flags[EnableContentRetentionFlag];
    if (!retentionEnabled) return;

    const retentionConfig = getRetentionConfig();
    if (!retentionConfig) return;

    if (this.isContentRetentionOnCooldown()) return;

    this.contentRetentionRunning = true;
    try {
      log.info(`[${this.constructor.name}] Running content retention (trigger: ${trigger})`, {
        retentionDays: retentionConfig.retentionDays,
      });

      const result = await applyContentRetention(retentionConfig.retentionDays);
      this.saveContentRetentionTimestamp();

      if (result.deletedItems > 0) {
        log.info(`[${this.constructor.name}] Content retention complete`, {
          trigger,
          deletedItems: result.deletedItems,
          deletedContentIds: result.deletedContentIds,
          affectedFeeds: result.affectedFeeds,
          retentionDays: retentionConfig.retentionDays,
        });
        ampli.purgeLocalDatabase({ purgeMethod: "contentRetention" });
        await recordEventAndFlush("db.contentRetention.completed", {
          db: {
            contentRetention: {
              trigger,
              deletedItems: result.deletedItems,
              deletedContentIds: result.deletedContentIds,
              affectedFeeds: result.affectedFeeds,
              retentionDays: retentionConfig.retentionDays,
              user: this.userMetadata,
            },
          },
        });
      }
    } catch (error) {
      log.error(`[${this.constructor.name}] Content retention failed`, { error });
    } finally {
      this.contentRetentionRunning = false;
    }
  }

  private isContentRetentionOnCooldown(): boolean {
    try {
      const stored = localStorage.getItem(ResetCoordinator.CONTENT_RETENTION_STORAGE_KEY);
      if (!stored) return false;
      const lastRun = DateTime.fromJSDate(new Date(stored));
      if (!lastRun.isValid) return false;
      return DateTime.now().diff(lastRun).toMillis() < ResetCoordinator.CONTENT_RETENTION_COOLDOWN_MS;
    } catch {
      return false;
    }
  }

  private saveContentRetentionTimestamp(): void {
    safeSetItem(localStorage, ResetCoordinator.CONTENT_RETENTION_STORAGE_KEY, new Date().toISOString());
  }
}
